import * as React from 'react';
import classNames from 'classnames';
import { isValidVin } from './utils/vinValidator';
import { formatVin } from './utils/format';
import { usaStates } from './utils/lookups';
import { IInstantOffersFormData } from './InstantOffersContainer';
import { ANALYTICS_CONSTANTS, trackEDDL } from '../../utilities/analytics';
import '../TextField/TextField.scss';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { SegmentedTabs } from '../segmentedTabs';

interface IInstantOffersFormProps {
    readonly?: boolean;
    hasInvalidLicensePlate: boolean;
    onSubmit: (event: any, formData: IInstantOffersFormData) => any;
}

const tabState = { PLATE: 0, VIN: 1 };

const TAB_PLATE_ID = 'LicensePlate';
const TAB_VIN_ID = 'VIN';

export default function InstantOffersForm(props: IInstantOffersFormProps) {
    const initialVin = '',
        initialPlate = '',
        initialState = '',
        initialZipCode = '';

    const [vin, setVin] = React.useState(initialVin);
    const [state, setState] = React.useState(initialState);
    const [plate, setPlate] = React.useState(initialPlate);
    const [zipCode, setZipCode] = React.useState(initialZipCode);

    const [vinError, setVinError] = React.useState('');
    const [stateError, setStateError] = React.useState('');
    const [plateError, setPlateError] = React.useState('');
    const [zipCodeError, setZipCodeError] = React.useState('');

    const [showInvalidLicensePlateMessage, setShowInvalidLicensePlateMessage] = React.useState(
        props.hasInvalidLicensePlate,
    );

    const [isValid, setIsValid] = React.useState(false);
    const [activeTab, setActiveTab] = React.useState(tabState.PLATE);

    React.useEffect(() => {
        if (
            zipCode &&
            zipCode.length == 5 &&
            ((activeTab === tabState.PLATE && state && plate && /[^0-9a-zA-Z]*/.test(plate)) ||
                (activeTab === tabState.VIN && vin && vin.length == 17 && isValidVin(vin)))
        )
            setIsValid(true);
        else setIsValid(false);
    }, [vin, state, plate, zipCode, activeTab]);

    React.useEffect(() => {
        if (props.hasInvalidLicensePlate) {
            setPlateError('A valid license plate must be entered');
            setStateError('');
            setIsValid(false);
        }
    }, [props.hasInvalidLicensePlate]);

    const plateValid = () => {
        if (!plate.length) {
            setPlateError('License plate must be entered');
        } else {
            console.log('plateValid: - else block');
            setPlateError('');
        }
    };

    const stateValid = () => {
        if (!state.length) setStateError('Please make a selection above');
        else setStateError('');
    };

    const zipValid = () => {
        if (zipCode === null || zipCode.length === 0) setZipCodeError('ZIP code must be entered');
        else if (zipCode.length > 0 && zipCode.length != 5) setZipCodeError('ZIP code must be 5 digits');
        else setZipCodeError('');
    };

    const vinValid = () => {
        if (vin.length != 17) setVinError('17 character VIN must be entered');
        else if (!isValidVin(vin))
            setVinError(
                'VIN must be a valid Vehicle Identification Number. Please check that you have entered the VIN correctly.',
            );
        else setVinError('');
    };

    const zipInput = (
        <div
            className={classNames('input-field input-field--zip', {
                ['collapsed-input']: activeTab === tabState.PLATE,
            })}
        >
            <TextField
                label="What's your ZIP code?"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setZipCode(e.target.value.replace(/[^0-9]/, ''))}
                value={zipCode}
                disabled={props.readonly}
                helperText={zipCodeError.length ? zipCodeError : ''}
                error={zipCodeError.length > 0}
                slotProps={{ htmlInput: { maxLength: 5 } }}
                type="tel"
                id="ico-form-zipcode"
                onBlur={zipValid}
                name="zipcode"
                fullWidth
            />
        </div>
    );

    const plateInput = (
        <Stack
            spacing={2}
            id={`${TAB_PLATE_ID}_panel`}
            hidden={activeTab != tabState.PLATE}
            role="tabpanel"
            aria-labelledby={TAB_PLATE_ID}
        >
            <TextField
                label="License plate number"
                value={plate}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setPlate(e.target.value.replace(/[^0-9a-zA-Z \\-]/, ''))
                }
                onBlur={plateValid}
                slotProps={{ htmlInput: { maxLength: 10 } }}
                helperText={plateError.length ? plateError : ''}
                error={plateError.length > 0}
                id="ico-form-plate"
                name="plate"
                fullWidth
            />

            <TextField
                select
                label="State"
                defaultValue={state}
                id="ico-state-select"
                onChange={(e: any) => setState(e.target.value)}
                onBlur={stateValid}
                name="state"
                error={stateError.length > 0}
                helperText={stateError.length ? stateError : ''}
                fullWidth
            >
                {usaStates.map((state) => (
                    <option key={state.value + state.label} value={state.value}>
                        {state.label}
                    </option>
                ))}
            </TextField>

            {showInvalidLicensePlateMessage && (
                <hzn-global-alert content=" We could not find this license plate."></hzn-global-alert>
            )}

            {zipInput}
        </Stack>
    );

    const vinInput = (
        <>
            <Stack
                id={`${TAB_VIN_ID}_panel`}
                spacing={2}
                hidden={activeTab != tabState.VIN}
                role="tabpanel"
                aria-labelledby="ICO: Vehicle Profile"
                sx={{ mb: 3 }}
            >
                <TextField
                    label="VIN"
                    value={vin}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setVin(formatVin(e.target.value));
                    }}
                    slotProps={{ htmlInput: { maxLength: 17 } }}
                    helperText={vinError.length ? vinError : ''}
                    error={vinError.length > 1}
                    id="ico-form-vin"
                    name="vin"
                    onBlur={vinValid}
                />
                <div className="vin-zip-input">{zipInput}</div>
            </Stack>
        </>
    );

    const renderInput = () => {
        switch (activeTab) {
            case tabState.PLATE:
                return plateInput;
            case tabState.VIN:
                return vinInput;
            default:
                return plateInput;
        }
    };

    return (
        <Paper
            elevation={2}
            sx={{
                borderRadius: 2,
                minHeight: { xs: '32.55rem', md: '38rem' },
                maxWidth: { sm: '24rem' },
                padding: 4,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Stack spacing={0.5} sx={{ mb: 3 }}>
                <Typography variant="h6" component="h2">
                    Sell/trade in
                </Typography>
                <Typography sx={{ typography: { xs: 'h5', md: 'h4' } }}>
                    Real offers, <Box component="br" sx={{ display: { xs: 'none', md: 'initial' } }} />
                    really fast
                </Typography>
            </Stack>
            <SegmentedTabs
                id="ICO: Vehicle Profile"
                value={activeTab}
                aria-label="Instant Offers form tabs"
                sx={{ mb: 3 }}
            >
                <Tab
                    label="Plate"
                    id={TAB_PLATE_ID}
                    onClick={() => {
                        trackEDDL({
                            event: ANALYTICS_CONSTANTS.EVENTS.CLICK.CTA,
                            linkDetails: {
                                name: 'License Plate',
                                position: 'Homepage',
                            },
                        });
                        setActiveTab(tabState.PLATE);
                    }}
                />
                <Tab
                    label="VIN"
                    id={TAB_VIN_ID}
                    onClick={() => {
                        trackEDDL({
                            event: ANALYTICS_CONSTANTS.EVENTS.CLICK.CTA,
                            linkDetails: {
                                name: 'VIN',
                                position: 'Homepage',
                            },
                        });
                        setActiveTab(tabState.VIN);
                    }}
                />
            </SegmentedTabs>
            <Box
                component="form"
                sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                }}
                onSubmit={(e) => {
                    trackEDDL({
                        event: ANALYTICS_CONSTANTS.EVENTS.CLICK.CTA,
                        linkDetails: {
                            name: 'Get started',
                            position: 'Homepage',
                        },
                    });

                    let type: IInstantOffersFormData['type'];
                    if (vin && activeTab === tabState.VIN) {
                        type = 'VIN';
                    } else if (plate && activeTab === tabState.PLATE) {
                        type = 'PLATE';
                    } else {
                        return;
                    }

                    props.onSubmit(e, {
                        isValid: isValid,
                        type: type,
                        state: state,
                        zipCode: zipCode,
                        vin: vin,
                        plate: plate,
                    });
                }}
            >
                <Stack spacing={3} sx={{ flexGrow: 1, justifyContent: 'space-between' }} useFlexGap>
                    {renderInput()}
                    <Stack spacing={1} sx={{ mt: 'auto' }}>
                        <Button
                            variant="contained"
                            type="submit"
                            size="large"
                            fullWidth
                            disabled={!isValid || undefined}
                        >
                            Get started
                        </Button>
                        <Typography
                            variant="caption"
                            component="div"
                            color="textSecondary"
                            sx={{ textAlign: 'center' }}
                        >
                            Most qualify. Some we&apos;ll ask to verify in-person.
                        </Typography>
                    </Stack>
                </Stack>
            </Box>
        </Paper>
    );
}
